// M2 — persona detection from quiz answers.
//
// Five orthogonal personas derived from how a user answered the quiz.
// A user can be tagged with at most one persona (we take the strongest
// signal) and an "unknown" fallback when no pattern dominates. The label
// is a UX device — it lets us say "we found 8 comuni that fit a
// family-with-kids profile" instead of "8 comuni passed the filter".
//
// Detection is intentionally rule-based, not learned: this is a small
// state space and the rules are inspectable + adjustable. Each persona
// has weights for the comune-level scoring boost (M2 scoreComune).

import type { Answers } from '@/features/quiz/types';

export type PersonaCode = 'family' | 'nomad' | 'sport' | 'quiet' | 'urban';

export interface PersonaDef {
  code: PersonaCode;
  /** i18n key for the human-readable label, e.g. "Family with kids". */
  labelKey: string;
  /** Per-feature multipliers used by scoreComune to nudge a base score. */
  bonus: {
    /** Schools nearby (km, lower = better). Multiplier ≥ 1 = personas that care. */
    school_proximity: number;
    /** Hospitals + pharmacies nearby. */
    health_proximity: number;
    /** Ski lift / alpine hut access — sport / outdoor profile. */
    outdoor_proximity: number;
    /** Train + airport access — nomad profile. */
    transit_proximity: number;
    /** Internet quality — nomad profile. */
    internet: number;
    /** Population (small = quiet, large = urban). >1 favours small, <1 favours large. */
    quiet_bias: number;
  };
}

export const PERSONAS: Record<PersonaCode, PersonaDef> = {
  family: {
    code: 'family',
    labelKey: 'persona.family',
    bonus: {
      school_proximity: 1.5,
      health_proximity: 1.4,
      outdoor_proximity: 1.0,
      transit_proximity: 1.0,
      internet: 1.0,
      quiet_bias: 1.1,
    },
  },
  nomad: {
    code: 'nomad',
    labelKey: 'persona.nomad',
    bonus: {
      school_proximity: 1.0,
      health_proximity: 1.0,
      outdoor_proximity: 1.0,
      transit_proximity: 1.4,
      internet: 1.5,
      quiet_bias: 1.0,
    },
  },
  sport: {
    code: 'sport',
    labelKey: 'persona.sport',
    bonus: {
      school_proximity: 1.0,
      health_proximity: 1.1,
      outdoor_proximity: 1.6,
      transit_proximity: 1.0,
      internet: 1.0,
      quiet_bias: 1.0,
    },
  },
  quiet: {
    code: 'quiet',
    labelKey: 'persona.quiet',
    bonus: {
      school_proximity: 1.0,
      health_proximity: 1.2,
      outdoor_proximity: 1.2,
      transit_proximity: 1.0,
      internet: 1.0,
      quiet_bias: 1.3,
    },
  },
  urban: {
    code: 'urban',
    labelKey: 'persona.urban',
    bonus: {
      school_proximity: 1.0,
      health_proximity: 1.2,
      outdoor_proximity: 1.0,
      transit_proximity: 1.3,
      internet: 1.2,
      quiet_bias: 0.8,
    },
  },
};

/**
 * Detect a persona from quiz answers. Returns null when no pattern
 * dominates — callers should treat that as "use the unweighted base score".
 *
 * The detection is conservative: we only assign a persona when we have a
 * clear signal (e.g. kids in family + town/rural urban_density). If two
 * signals tie we leave it unassigned rather than guess.
 */
export function detectPersona(answers: Answers): PersonaCode | null {
  const family = answers.family?.optionIds ?? [];
  const urbanDensity = answers.urban_density?.optionIds ?? [];
  const income = answers.income?.optionIds ?? [];
  const priorities = answers.priorities?.optionIds ?? [];
  const climate = answers.climate?.optionIds ?? [];
  const dailyActivities = priorities; // alias for clarity

  const hasKids = family.includes('kids-preschool') || family.includes('kids-school');

  // Family: kids + non-capital living. The "non-capital" filter prevents
  // promoting big cities to family-tagged users — they'd lose the schools-
  // proximity advantage.
  if (hasKids && (urbanDensity.includes('town') || urbanDensity.includes('mid_city') || urbanDensity.length === 0)) {
    return 'family';
  }

  // Nomad: remote income + (no kids or solo family + town/mid). The
  // internet weight matters most for this profile.
  if (income.includes('remote') && !hasKids) {
    return 'nomad';
  }

  // Sport / outdoor: alpine climate + sports priority + likely town/rural.
  if (
    (climate.includes('alps') || climate.includes('northern')) &&
    (dailyActivities.includes('nature') || dailyActivities.includes('sports'))
  ) {
    return 'sport';
  }

  // Quiet-village: rural urban_density + nature priority + small-pop preference.
  if (urbanDensity.includes('coastal') === false && (urbanDensity.includes('town') || urbanDensity.length === 0)) {
    if (dailyActivities.includes('nature') && !income.includes('local')) {
      return 'quiet';
    }
  }

  // Urban: capital/big-city + culture priority.
  if (urbanDensity.includes('capital') || (urbanDensity.includes('mid_city') && dailyActivities.includes('culture'))) {
    return 'urban';
  }

  return null;
}
